import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { cn } from '@/lib/utils'
import { CheckCircle2Icon, AlertTriangleIcon, InfoIcon, XIcon } from 'lucide-react'

export type ToastKind = 'success' | 'error' | 'info'
export type Toast = {
	id: string
	kind: ToastKind
	title: string
	description?: string
	durationMs: number
}

type ToastContextValue = {
	toast: (input: Omit<Toast, 'id' | 'durationMs'> & { durationMs?: number }) => void
}

const ToastContext = createContext<ToastContextValue | null>(null)
let toastFn: ToastContextValue['toast'] | null = null

export function toast(input: Parameters<ToastContextValue['toast']>[0]) {
	if (toastFn) toastFn(input)
}

export function Toaster() {
	const [items, setItems] = useState<Toast[]>([])

	const push = useCallback(
		(input: Omit<Toast, 'id' | 'durationMs'> & { durationMs?: number }) => {
			const id = Math.random().toString(36).slice(2)
			const next: Toast = {
				id,
				kind: input.kind,
				title: input.title,
				description: input.description,
				durationMs: input.durationMs ?? 4000,
			}
			setItems((prev) => [...prev, next])
		},
		[],
	)

	useEffect(() => {
		toastFn = push
		return () => {
			toastFn = null
		}
	}, [push])

	useEffect(() => {
		if (items.length === 0) return
		const next = items[0]
		const t = window.setTimeout(() => {
			setItems((prev) => prev.filter((x) => x.id !== next.id))
		}, next.durationMs)
		return () => window.clearTimeout(t)
	}, [items])

	const value = useMemo(() => ({ toast: push }), [push])

	return (
		<ToastContext.Provider value={value}>
			<div
				className="pointer-events-none fixed bottom-4 left-1/2 z-[100] flex w-[min(420px,calc(100%-2rem))] -translate-x-1/2 flex-col gap-2"
				dir="rtl"
			>
				{items.map((item) => (
					<div
						key={item.id}
						className={cn(
							'pointer-events-auto flex items-start gap-3 rounded-xl border bg-card px-4 py-3 shadow-lg animate-in slide-in-from-bottom-2',
							item.kind === 'success' &&
								'border-emerald-500/30 bg-emerald-50 dark:bg-emerald-950/60',
							item.kind === 'error' && 'border-destructive/30 bg-destructive/10',
							item.kind === 'info' && 'border-border',
						)}
					>
						<div className="mt-0.5 shrink-0">
							{item.kind === 'success' ? (
								<CheckCircle2Icon className="size-5 text-emerald-600 dark:text-emerald-400" />
							) : item.kind === 'error' ? (
								<AlertTriangleIcon className="size-5 text-destructive" />
							) : (
								<InfoIcon className="size-5 text-muted-foreground" />
							)}
						</div>
						<div className="flex-1 text-sm">
							<div className="font-semibold text-foreground">{item.title}</div>
							{item.description ? (
								<div className="mt-0.5 text-muted-foreground">{item.description}</div>
							) : null}
						</div>
						<button
							type="button"
							aria-label="إغلاق"
							className="shrink-0 rounded-md p-1 text-muted-foreground hover:bg-muted"
							onClick={() => setItems((prev) => prev.filter((x) => x.id !== item.id))}
						>
							<XIcon className="size-4" />
						</button>
					</div>
				))}
			</div>
		</ToastContext.Provider>
	)
}

export function useToast() {
	const ctx = useContext(ToastContext)
	if (!ctx) {
		return { toast }
	}
	return ctx
}
